import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    DataService,
    NotificationService
} from '@vendure/admin-ui/core';
import { safeJSONParse } from '../utils';
import { defaultProductListingData, PageType, ProductListingData, ProductListingSourceType } from '../ui-types';
import { Subject, Subscription, takeUntil } from 'rxjs';

@Component({
    selector: 'product-listing-form',
    template: `
    <vdr-card>
      <!-- Product Listing Source Dropdown -->
      <div class="clr-row mb-2">
        <vdr-form-field [label]="'Product Listing Type'" class="clr-col-12">
          <select [formControl]="productListingSourceControl">
            <option *ngIf="canUseInheritedCollection" value="dynamic">Dynamic</option>
            <option value="all">All Products</option>
            <option value="collection">By Collection</option>
            <option value="facetValue">By Facet Values</option>
          </select>
        </vdr-form-field>

        <clr-alert
          *ngIf="!canUseInheritedCollection && productListingData?.useInheritedCollection"
          clrAlertType="warning"
          clrAlertSize="compact"
        >
          <clr-alert-item>
            <span class="clr-alert-text">
              Dynamic option applies only to Collection pages.
              This option will be ignored unless the page type is changed.
            </span>
          </clr-alert-item>
        </clr-alert>
        <div *ngIf="productListingSourceControl.value === 'dynamic'" class="clr-col-12 mt-1">
          <p class="hint-text">Automatically displays the collection currently being viewed by the customer.</p>
        </div>
      </div>

      <!-- Collection Selector -->
      <div class="clr-row mb-2" *ngIf="productListingSourceControl.value === 'collection'">
        <div class="clr-col-12">
          <vdr-form-field [label]="'Select Collection'">
            <vdr-dynamic-form-input
                [control]="collectionInfoControl"
                [def]="{
                    type: 'relation',
                    entity: 'Collection',
                    ui: {
                        component: 'relation-collection-selector'
                    }
                }"
            >
            </vdr-dynamic-form-input>
          </vdr-form-field>
        </div>
      </div>
      <!-- Facet Selector -->
      <div class="clr-row mb-2" *ngIf="productListingSourceControl.value === 'facetValue'">
        <div class="clr-col-12">
          <vdr-form-field [label]="'Select Facet Values'">
            <vdr-dynamic-form-input #facetValuesInput
              [control]="facetValuesControl"
              [def]="{
                list: true,
                ui: {
                  component: 'facet-value-form-input'
                }
              }">
            </vdr-dynamic-form-input>
          </vdr-form-field>
        </div>
      </div>
    </vdr-card>
    `,
    styleUrls: ['../styles.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class ProductListingComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
    readonly: boolean;
    config: StringCustomFieldConfig;
    formControl: FormControl;

    productListingSourceControl = new FormControl<ProductListingSourceType>('all');
    collectionInfoControl = new UntypedFormControl();
    facetValuesControl = new FormControl();

    pageType: PageType;
    productListingData: ProductListingData;
    private destroyed$ = new Subject<void>();

    get canUseInheritedCollection(): boolean {
      return this.pageType === PageType.Collection || this.pageType === PageType.CollectionSpecific;
    }

    constructor(
      private notificationService: NotificationService,
      private changeDetector: ChangeDetectorRef,
      private dataService: DataService,
    ) {}

    ngOnInit() {
        this.productListingData = safeJSONParse<ProductListingData>(
            this.formControl.value,
            this.notificationService,
            defaultProductListingData,
        )!;
        this.pageType = this.config.ui?.options?.pageType || PageType.General;
        this.initializeFormControls();
        this.handlePageTypeChange();
        this.config.ui?.changes?.pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
          if(data && data !== this.pageType) {
            this.pageType = data;
            this.handlePageTypeChange();
            this.changeDetector.detectChanges();
          }
        });
        const initialCollectionId = this.productListingData?.collectionId;
        if(this.productListingData?.productListingType === 'collection' && initialCollectionId) {
            this.dataService.collection.getCollections({
                filter: {
                    id: { eq: initialCollectionId.toString() },
                },
            })
            .mapSingle(data => data.collections?.items[0])
            .pipe(takeUntil(this.destroyed$)).subscribe(collection => {
                if (collection) {
                    this.collectionInfoControl.setValue(collection, { emitEvent: false });
                    this.changeDetector.markForCheck();
                }
            });
        }
    }

    ngAfterViewInit() {
        this.productListingSourceControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onProductListingSourceChange);
        this.collectionInfoControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onCollectionInfoChange);
        this.facetValuesControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onFacetValuesChange);
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private initializeFormControls() {
        const sourceType = this.getSourceTypeFromData();
        this.productListingSourceControl.setValue(sourceType, { emitEvent: false });
        this.facetValuesControl.setValue(this.productListingData?.facetValueIds || [], { emitEvent: false });
    }

    private getSourceTypeFromData(): ProductListingSourceType {
        if (this.productListingData?.useInheritedCollection && this.canUseInheritedCollection) {
            return 'dynamic';
        }
        const listingType = this.productListingData?.productListingType;
        if (listingType === 'collection' || listingType === 'facetValue') {
            return listingType;
        }
        return 'all';
    }

    private handlePageTypeChange() {
        // If currently set to 'dynamic' but no longer allowed, switch to 'all'
        if (this.productListingSourceControl.value === 'dynamic' && !this.canUseInheritedCollection) {
            this.productListingSourceControl.setValue('all', { emitEvent: false });
            this.productListingData.useInheritedCollection = false;
            this.productListingData.productListingType = 'all';
            this.updateJsonString();
        }
    }

    private onProductListingSourceChange = (value: ProductListingSourceType | null) => {
        const useInherited = value === 'dynamic' && this.canUseInheritedCollection;

        // Update useInheritedCollection
        this.productListingData!.useInheritedCollection = useInherited;

        if (useInherited) {
            // Clear all listing data when switching to dynamic
            this.productListingData!.productListingType = '';
            this.productListingData!.collectionId = null;
            this.productListingData!.facetValueIds = [];
            this.collectionInfoControl.setValue(null, { emitEvent: false });
            this.facetValuesControl.setValue([], { emitEvent: false });
        } else {
            // Update productListingType based on selection
            const newListingType = value === 'dynamic' ? 'all' : value;

            if (this.productListingData?.productListingType !== newListingType) {
                this.productListingData!.productListingType = newListingType!;

                // Clear collection data if not collection type
                if (newListingType !== 'collection') {
                    this.productListingData!.collectionId = null;
                    this.collectionInfoControl.setValue(null, { emitEvent: false });
                }

                // Clear facet data if not facetValue type
                if (newListingType !== 'facetValue') {
                    this.productListingData!.facetValueIds = [];
                    this.facetValuesControl.setValue([], { emitEvent: false });
                }
            }
        }

        this.updateJsonString();
    };

    private onCollectionInfoChange = (value: any) => {
        const collectionId = value ? value.id : null;
        if (this.productListingData?.collectionId !== collectionId) {
            this.productListingData!.collectionId = collectionId;
            this.updateJsonString();
        }
    };

    private onFacetValuesChange = (value: any) => {
        let facetValueIds: string[] = [];
        if (Array.isArray(value)) {
            facetValueIds = value.map(item => item.id ? item.id : item);
        }
        if (JSON.stringify(this.productListingData?.facetValueIds) !== JSON.stringify(facetValueIds)) {
            this.productListingData!.facetValueIds = facetValueIds;
            this.updateJsonString();
        }
    };

    private updateJsonString() {
        if (!this.canUseInheritedCollection) {
          this.productListingData.useInheritedCollection = false;
        }
        const jsonString = JSON.stringify(this.productListingData);
        this.formControl.setValue(jsonString);
        this.formControl.markAsDirty();
    }
}
